import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/lib/settings";

async function fileToBase64(file, max = 512) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let { width, height } = img;
        if (width > max || height > max) { const s = Math.min(max / width, max / height); width = Math.floor(width * s); height = Math.floor(height * s); }
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/png"));
      };
      img.onerror = reject; img.src = r.result;
    };
    r.onerror = reject; r.readAsDataURL(file);
  });
}

export default function SettingsTab() {
  const { reload } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(r => setS(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const onLogoUpload = async (file) => {
    try { const b64 = await fileToBase64(file, 512); setS({ ...s, logo_url: b64 }); toast.success("Logo staged — click Save to apply"); }
    catch { toast.error("Couldn't read logo"); }
  };
  const onAboutPhotoUpload = async (file) => {
    try { const b64 = await fileToBase64(file, 900); setS({ ...s, about_photo: b64 }); toast.success("Photo staged — click Save to apply"); }
    catch { toast.error("Couldn't read photo"); }
  };
  const addSlide = () => setS({ ...s, hero_slides: [...(s.hero_slides || []), { image: "", title: "", subtitle: "" }] });
  const removeSlide = (i) => setS({ ...s, hero_slides: s.hero_slides.filter((_, j) => j !== i) });
  const setSlide = (i, k, v) => { const c = [...(s.hero_slides || [])]; c[i] = { ...c[i], [k]: v }; setS({ ...s, hero_slides: c }); };
  const uploadSlideImage = async (i, file) => { try { const b64 = await fileToBase64(file, 1600); setSlide(i, "image", b64); } catch { toast.error("Upload failed"); } };

  if (!s) return <div className="p-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Store Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Business info, branding, hero slideshow, policies, notifications.</p>
      </div>
      <Tabs defaultValue="branding">
        <TabsList>
          <TabsTrigger value="branding" data-testid="settings-tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="business" data-testid="settings-tab-business">Business</TabsTrigger>
          <TabsTrigger value="hero" data-testid="settings-tab-hero">Hero slides</TabsTrigger>
          <TabsTrigger value="about" data-testid="settings-tab-about">About page</TabsTrigger>
          <TabsTrigger value="policies" data-testid="settings-tab-policies">Policies</TabsTrigger>
          <TabsTrigger value="notifs" data-testid="settings-tab-notifs">Notifications</TabsTrigger>
        </TabsList>
        <TabsContent value="branding" className="pt-4 space-y-4">
          <Card className="p-5">
            <div className="font-display font-semibold mb-3">Logo</div>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl border border-border bg-secondary flex items-center justify-center overflow-hidden">
                {s.logo_url ? <img src={s.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-muted-foreground">No custom logo</span>}
              </div>
              <div className="space-y-2">
                <label className="inline-block">
                  <Button variant="outline" asChild><span><Upload className="h-4 w-4 mr-2" />Upload image</span></Button>
                  <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onLogoUpload(e.target.files[0])} data-testid="logo-upload-input" />
                </label>
                <div className="text-xs text-muted-foreground">PNG/SVG/JPG · Auto-resized to 512px · Applies everywhere after save.</div>
                {s.logo_url && <Button variant="ghost" size="sm" onClick={() => setS({ ...s, logo_url: "" })} data-testid="logo-clear">Clear (use default eagle)</Button>}
              </div>
            </div>
          </Card>
          <Card className="p-5 space-y-3">
            <div><Label>Tagline / one-liner</Label><Input value={s.tagline || ""} onChange={(e) => setS({ ...s, tagline: e.target.value })} placeholder="Eyes on your property. Always." data-testid="settings-tagline" /></div>
          </Card>
        </TabsContent>
        <TabsContent value="business" className="pt-4 space-y-4">
          <Card className="p-5 space-y-3">
            <div><Label>Business phone</Label><Input value={s.business_phone || ""} onChange={(e) => setS({ ...s, business_phone: e.target.value })} data-testid="settings-phone" /></div>
            <div><Label>Business email</Label><Input value={s.business_email || ""} onChange={(e) => setS({ ...s, business_email: e.target.value })} data-testid="settings-email" /></div>
            <div><Label>Business address</Label><Input value={s.business_address || ""} onChange={(e) => setS({ ...s, business_address: e.target.value })} data-testid="settings-address" /></div>
            <div><Label>Business hours</Label><Input value={s.business_hours || ""} onChange={(e) => setS({ ...s, business_hours: e.target.value })} placeholder="Mon–Sat · 7am–6pm" /></div>
            <div><Label>Google review URL</Label><Input value={s.google_review_url || ""} onChange={(e) => setS({ ...s, google_review_url: e.target.value })} data-testid="settings-review-url" /></div>
          </Card>
        </TabsContent>
        <TabsContent value="hero" className="pt-4 space-y-3">
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold">Hero slides</div>
              <Button size="sm" variant="outline" onClick={addSlide} data-testid="hero-add-slide"><Plus className="h-4 w-4 mr-1" />Add slide</Button>
            </div>
            {(!s.hero_slides || s.hero_slides.length === 0) && <div className="text-sm text-muted-foreground">No slides yet — the default hero will be used.</div>}
            {(s.hero_slides || []).map((sl, i) => (
              <div key={i} className="p-4 rounded-lg bg-secondary/50 border border-border space-y-3 relative">
                <button onClick={() => removeSlide(i)} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-red-500/15 text-red-600 hover:bg-red-500/25 flex items-center justify-center"><X className="h-3 w-3" /></button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Image</Label>
                    <div className="w-full aspect-video rounded-lg bg-secondary border border-border overflow-hidden flex items-center justify-center">
                      {sl.image ? <img src={sl.image} alt="" className="w-full h-full object-cover" /> : <label className="cursor-pointer text-xs text-muted-foreground">
                        Upload
                        <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadSlideImage(i, e.target.files[0])} />
                      </label>}
                    </div>
                    {sl.image && <Button size="sm" variant="ghost" onClick={() => setSlide(i, "image", "")}>Change</Button>}
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <div><Label>Title</Label><Input value={sl.title || ""} onChange={(e) => setSlide(i, "title", e.target.value)} data-testid={`slide-title-${i}`} /></div>
                    <div><Label>Subtitle</Label><Input value={sl.subtitle || ""} onChange={(e) => setSlide(i, "subtitle", e.target.value)} /></div>
                    <Input placeholder="Or paste an image URL" value={sl.image?.startsWith("http") ? sl.image : ""} onChange={(e) => setSlide(i, "image", e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
        <TabsContent value="about" className="pt-4 space-y-4">
          <Card className="p-5">
            <div className="font-display font-semibold mb-3">Owner photo</div>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 rounded-xl border border-border bg-secondary flex items-center justify-center overflow-hidden">
                {s.about_photo ? <img src={s.about_photo} alt="Owner" className="w-full h-full object-cover" /> : <span className="text-xs text-muted-foreground text-center px-2">No photo uploaded</span>}
              </div>
              <div className="space-y-2">
                <label className="inline-block">
                  <Button variant="outline" asChild><span><Upload className="h-4 w-4 mr-2" />Upload photo</span></Button>
                  <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onAboutPhotoUpload(e.target.files[0])} data-testid="about-photo-upload-input" />
                </label>
                <div className="text-xs text-muted-foreground">Shown on the About page · auto-resized to 900px.</div>
                {s.about_photo && <Button variant="ghost" size="sm" onClick={() => setS({ ...s, about_photo: "" })} data-testid="about-photo-clear">Remove photo</Button>}
              </div>
            </div>
          </Card>
          <Card className="p-5 space-y-3">
            <div><Label>Years of experience</Label><Input type="number" min="0" value={s.about_years_experience ?? 15} onChange={(e) => setS({ ...s, about_years_experience: parseInt(e.target.value || 0) })} className="w-32" data-testid="settings-about-years" /></div>
            <div>
              <Label>Bio</Label>
              <Textarea rows={6} value={s.about_bio || ""} onChange={(e) => setS({ ...s, about_bio: e.target.value })} placeholder="Leave blank to use the default bio." data-testid="settings-about-bio" />
              <div className="text-xs text-muted-foreground mt-1">Shown on the About page. Leave blank to use the default write-up.</div>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="policies" className="pt-4 space-y-3">
          <Card className="p-5 space-y-3">
            <div><Label>Privacy policy</Label><Textarea rows={5} value={s.privacy_policy || ""} onChange={(e) => setS({ ...s, privacy_policy: e.target.value })} /></div>
            <div><Label>Terms of service</Label><Textarea rows={5} value={s.terms_of_service || ""} onChange={(e) => setS({ ...s, terms_of_service: e.target.value })} /></div>
            <div><Label>Refund policy</Label><Textarea rows={5} value={s.refund_policy || ""} onChange={(e) => setS({ ...s, refund_policy: e.target.value })} /></div>
          </Card>
        </TabsContent>
        <TabsContent value="notifs" className="pt-4 space-y-3">
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between"><Label>Email me on new quote</Label><Switch checked={!!s.notification_email_new_quote} onCheckedChange={(v) => setS({ ...s, notification_email_new_quote: v })} /></div>
            <div className="flex items-center justify-between"><Label>Email me on new chat message</Label><Switch checked={!!s.notification_email_new_chat} onCheckedChange={(v) => setS({ ...s, notification_email_new_chat: v })} /></div>
            <div className="flex items-center justify-between"><Label>Email me on booking events</Label><Switch checked={!!s.notification_email_booking} onCheckedChange={(v) => setS({ ...s, notification_email_booking: v })} /></div>
          </Card>
          <Card className="p-5">
            <div className="font-display font-semibold mb-3">Integrations</div>
            <div className="flex items-center justify-between py-3 border-b border-border"><div><div className="font-medium">Resend Email</div><div className="text-xs text-muted-foreground">Set RESEND_API_KEY in backend/.env</div></div><Badge variant="outline">Placeholder</Badge></div>
            <div className="flex items-center justify-between py-3"><div><div className="font-medium">Xero Accounting</div><div className="text-xs text-muted-foreground">OAuth requires Client ID/Secret</div></div><Badge variant="outline">Placeholder</Badge></div>
          </Card>
        </TabsContent>
      </Tabs>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save settings
        </Button>
      </div>
    </div>
  );
}